import { useEffect, useState } from "react";
import {
  motion,
  AnimatePresence,
} from "framer-motion";
import {
  ChevronDown,
  Mail,
  Phone,
  Building2,
  GraduationCap,
  BookOpen,
  Award,
  FileText,
  Globe,
  User,
  BookMarked,
  Presentation,
} from "lucide-react";
import { useParams } from "react-router-dom";
import LogoFallback from "/assets/img/bubbanlogo.jpg";
import axios from "axios";

export default function ProfilePage({
  logoSrc = LogoFallback,
  pageTitle = "Teacher Profile | Bandarban University",
}) {
  const { id } = useParams();

  const [faculty, setFaculty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openTab, setOpenTab] = useState(null);
  const [error, setError] = useState("");

  const toggleTab = (tab) => {
    setOpenTab(openTab === tab ? null : tab);
  };

  const facultyInfo = async () => {
    setLoading(true);
    setError("");

    try {
      const payload = {
        userId: import.meta.env.VITE_API_USER,
        password: import.meta.env.VITE_API_PASS,
        teacher_id: id,
      };

      const api = axios.create({
        baseURL: import.meta.env.VITE_API_BASE_URL,
        headers: {
          "Content-Type": "application/json",
        },
      });

      const response = await api.post("/teacher-info", payload);

      setFaculty(response.data?.[0] || null);
    } catch (err) {
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    facultyInfo();
  }, [id]);

  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  const profileImage = faculty?.picture
    ? `${import.meta.env.VITE_API_SOURCE_URL}${faculty.picture}`
    : `${import.meta.env.VITE_API_SOURCE_URL}assets/images/teacher.jpg`;

  const accordionItems = [
    {
      id: 1,
      title: "Education",
      icon: GraduationCap,
      content: faculty?.qualification,
    },
    {
      id: 2,
      title: "Teaching Expertise",
      icon: Presentation,
      content: faculty?.teaching_expertise,
    },
    {
      id: 3,
      title: "Research Interest",
      icon: BookOpen,
      content: faculty?.research_interest,
    },
    {
      id: 4,
      title: "Journal Publications",
      icon: FileText,
      content: faculty?.journal_publication,
    },
    {
      id: 5,
      title: "Conference Publications",
      icon: Presentation,
      content: faculty?.conference_publication,
    },
    {
      id: 6,
      title: "Books & Chapters",
      icon: BookMarked,
      content: faculty?.book,
    },
    {
      id: 7,
      title: "Awards & Rewards",
      icon: Award,
      content: faculty?.project_award,
    },
    {
      id: 8,
      title: "Other Activities",
      icon: Globe,
      content: faculty?.others,
    },
  ];

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-green-50 via-white to-amber-50">
        <motion.div
          animate={{
            rotate: 360,
          }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: "linear",
          }}
          className="h-14 w-14 rounded-full border-4 border-green-100 border-t-[#07552e]"
        />
      </div>
    );
  }

  if (error || !faculty) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-6">
        <div className="rounded-3xl bg-white p-10 text-center shadow-xl">
          <h2 className="text-xl font-bold text-red-600">
            Faculty Profile Not Found
          </h2>

          <p className="mt-3 text-gray-500">
            {error || "Faculty information could not be loaded."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-hidden bg-[#f8faf9]">

      {/* ================= HERO ================= */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#064526] via-[#07552e] to-[#0b7340] px-6 py-20">

        {/* Animated Background */}
        <motion.div
          animate={{
            x: [0, 50, 0],
            y: [0, 30, 0],
          }}
          transition={{
            duration: 12,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="absolute -left-32 -top-32 h-96 w-96 rounded-full bg-[#d99b16]/20 blur-3xl"
        />

        <motion.div
          animate={{
            x: [0, -40, 0],
            y: [0, -30, 0],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="absolute -bottom-32 -right-32 h-96 w-96 rounded-full bg-white/10 blur-3xl"
        />

        <div className="relative mx-auto max-w-7xl">

          <motion.div
            initial={{
              opacity: 0,
              y: -20,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 0.6,
            }}
            className="mb-14 flex flex-col items-center text-center"
          >

            <img
              src={logoSrc}
              alt="Bandarban University"
              className="mb-5 h-20 w-20 rounded-full bg-white p-2 shadow-xl"
            />

            <span className="rounded-full border border-white/20 bg-white/10 px-5 py-2 text-sm font-semibold tracking-wide text-white backdrop-blur">
              BANDARBAN UNIVERSITY
            </span>

            <h1 className="mt-5 text-3xl font-bold text-white md:text-5xl">
              Teacher Profile
            </h1>

          </motion.div>


          {/* ================= PROFILE HERO ================= */}
          <div className="grid items-center gap-12 lg:grid-cols-[420px_1fr]">

            {/* IMAGE */}
            <motion.div
              initial={{
                opacity: 0,
                x: -60,
              }}
              animate={{
                opacity: 1,
                x: 0,
              }}
              transition={{
                duration: 0.8,
              }}
              className="relative mx-auto w-full max-w-md"
            >

              <motion.div
                animate={{
                  y: [0, -10, 0],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="absolute -inset-5 rounded-[2.5rem] bg-[#d99b16]/20 blur-2xl"
              />

              <motion.div
                whileHover={{
                  y: -8,
                }}
                className="relative overflow-hidden rounded-[2.5rem] border border-white/20 bg-white/10 p-3 shadow-2xl backdrop-blur"
              >

                <div className="relative overflow-hidden rounded-[2rem]">

                  <motion.img
                    src={profileImage}
                    alt={faculty.name}
                    whileHover={{
                      scale: 1.08,
                    }}
                    transition={{
                      duration: 0.6,
                    }}
                    className="h-[480px] w-full object-cover object-top"
                  />

                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />

                  <div className="absolute bottom-0 left-0 right-0 p-6">

                    <p className="text-sm font-semibold uppercase tracking-[0.2em] text-[#f4c24f]">
                      Faculty Member
                    </p>

                  </div>

                </div>

              </motion.div>

            </motion.div>


            {/* INFO */}
            <motion.div
              initial={{
                opacity: 0,
                x: 60,
              }}
              animate={{
                opacity: 1,
                x: 0,
              }}
              transition={{
                duration: 0.8,
                delay: 0.15,
              }}
              className="text-white"
            >

              <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-medium backdrop-blur">

                <span className="h-2 w-2 animate-pulse rounded-full bg-[#d99b16]" />

                Academic Profile

              </div>


              <h2 className="text-4xl font-bold leading-tight md:text-6xl">
                {faculty.name}
              </h2>


              <div className="mt-6 space-y-3">

                <div className="flex items-start gap-4">

                  <User className="mt-1 h-5 w-5 text-[#f4c24f]" />

                  <div>

                    <p className="text-sm text-white/60">
                      Designation
                    </p>

                    <p className="text-lg font-semibold">
                      {faculty.designation_name}
                    </p>

                  </div>

                </div>


                <div className="flex items-start gap-4">

                  <Building2 className="mt-1 h-5 w-5 text-[#f4c24f]" />

                  <div>

                    <p className="text-sm text-white/60">
                      Department
                    </p>

                    <p className="text-lg font-semibold">
                      {faculty.department_name}
                    </p>

                  </div>

                </div>

              </div>


              {/* CONTACT */}
              <div className="mt-8 grid gap-4 sm:grid-cols-2">

                {faculty.email && (
                  <a
                    href={`mailto:${faculty.email}`}
                    className="rounded-2xl border border-white/10 bg-white/10 p-5 backdrop-blur transition hover:-translate-y-1 hover:bg-white/20"
                  >

                    <Mail className="mb-3 h-6 w-6 text-[#f4c24f]" />

                    <p className="text-xs text-white/60">
                      Email Address
                    </p>

                    <p className="mt-1 break-all font-medium">
                      {faculty.email}
                    </p>

                  </a>
                )}


                {faculty.mobile_number && (
                  <a
                    href={`tel:${faculty.mobile_number}`}
                    className="rounded-2xl border border-white/10 bg-white/10 p-5 backdrop-blur transition hover:-translate-y-1 hover:bg-white/20"
                  >

                    <Phone className="mb-3 h-6 w-6 text-[#f4c24f]" />

                    <p className="text-xs text-white/60">
                      Mobile Number
                    </p>

                    <p className="mt-1 font-medium">
                      {faculty.mobile_number}
                    </p>

                  </a>
                )}

              </div>

            </motion.div>

          </div>

        </div>

      </section>


      {/* ================= ACADEMIC LINKS ================= */}
      <section className="relative z-10 mx-auto -mt-10 max-w-7xl px-6">

        <motion.div
          initial={{
            opacity: 0,
            y: 30,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          viewport={{
            once: true,
          }}
          className="rounded-3xl border border-gray-100 bg-white p-6 shadow-xl"
        >

          <div className="flex flex-wrap items-center justify-center gap-4">

            {faculty.google_scholar && (
              <a
                href={faculty.google_scholar}
                target="_blank"
                rel="noreferrer"
                className="rounded-xl bg-blue-600 px-6 py-3 text-sm font-semibold text-white shadow-md transition hover:-translate-y-1 hover:shadow-xl"
              >
                Google Scholar
              </a>
            )}

            {faculty.researchgate && (
              <a
                href={faculty.researchgate}
                target="_blank"
                rel="noreferrer"
                className="rounded-xl bg-[#00a99d] px-6 py-3 text-sm font-semibold text-white shadow-md transition hover:-translate-y-1 hover:shadow-xl"
              >
                ResearchGate
              </a>
            )}

            {faculty.orcid && (
              <a
                href={faculty.orcid}
                target="_blank"
                rel="noreferrer"
                className="rounded-xl bg-[#a6ce39] px-6 py-3 text-sm font-semibold text-white shadow-md transition hover:-translate-y-1 hover:shadow-xl"
              >
                ORCID
              </a>
            )}

            {faculty.scopus && (
              <a
                href={faculty.scopus}
                target="_blank"
                rel="noreferrer"
                className="rounded-xl bg-orange-600 px-6 py-3 text-sm font-semibold text-white shadow-md transition hover:-translate-y-1 hover:shadow-xl"
              >
                Scopus
              </a>
            )}

            {faculty.web_science && (
              <a
                href={faculty.web_science}
                target="_blank"
                rel="noreferrer"
                className="rounded-xl bg-purple-600 px-6 py-3 text-sm font-semibold text-white shadow-md transition hover:-translate-y-1 hover:shadow-xl"
              >
                Web of Science
              </a>
            )}

          </div>

        </motion.div>

      </section>


      {/* ================= ABOUT ================= */}
      {faculty.about && (
        <section className="mx-auto max-w-5xl px-6 py-20">

          <motion.div
            initial={{
              opacity: 0,
              y: 30,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
            }}
            className="rounded-[2rem] bg-white p-8 shadow-lg md:p-12"
          >

            <div className="mb-6 flex items-center gap-3">

              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-green-100">

                <User className="text-[#07552e]" />

              </div>

              <div>

                <p className="text-sm font-semibold uppercase tracking-wider text-[#d99b16]">
                  Biography
                </p>

                <h3 className="text-2xl font-bold text-gray-900">
                  About the Teacher
                </h3>

              </div>

            </div>

            <p className="leading-8 text-gray-600">
              {faculty.about}
            </p>

          </motion.div>

        </section>
      )}


      {/* ================= ACADEMIC DETAILS ================= */}
      <section className="mx-auto max-w-5xl px-6 pb-24">

        <motion.div
          initial={{
            opacity: 0,
            y: 30,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          viewport={{
            once: true,
          }}
          className="mb-10 text-center"
        >

          <span className="text-sm font-bold uppercase tracking-[0.2em] text-[#d99b16]">
            Academic Information
          </span>

          <h2 className="mt-3 text-3xl font-bold text-gray-900 md:text-4xl">
            Professional & Academic Details
          </h2>

        </motion.div>


        <div className="space-y-4">

          {accordionItems.map((item, index) => {
            const Icon = item.icon;

            if (!item.content) return null;

            return (
              <motion.div
                key={item.id}
                initial={{
                  opacity: 0,
                  y: 20,
                }}
                whileInView={{
                  opacity: 1,
                  y: 0,
                }}
                viewport={{
                  once: true,
                }}
                transition={{
                  delay: index * 0.05,
                }}
                className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm"
              >

                <button
                  onClick={() => toggleTab(item.id)}
                  className="flex w-full items-center justify-between p-6 text-left transition hover:bg-green-50"
                >

                  <div className="flex items-center gap-4">

                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-green-50">

                      <Icon className="h-5 w-5 text-[#07552e]" />

                    </div>

                    <span className="text-lg font-bold text-gray-800">
                      {item.title}
                    </span>

                  </div>


                  <motion.div
                    animate={{
                      rotate: openTab === item.id ? 180 : 0,
                    }}
                  >
                    <ChevronDown className="h-5 w-5 text-[#07552e]" />
                  </motion.div>

                </button>


                <AnimatePresence>

                  {openTab === item.id && (

                    <motion.div
                      initial={{
                        height: 0,
                        opacity: 0,
                      }}
                      animate={{
                        height: "auto",
                        opacity: 1,
                      }}
                      exit={{
                        height: 0,
                        opacity: 0,
                      }}
                      transition={{
                        duration: 0.35,
                      }}
                      className="overflow-hidden"
                    >

                      <div className="border-t border-gray-100 px-6 pb-6 pt-5">

                        <div className="space-y-3 text-gray-600">

                          {item.content
                            .split("\n")
                            .filter(Boolean)
                            .map((content, contentIndex) => (

                              <div
                                key={contentIndex}
                                className="flex gap-3 leading-7"
                              >

                                <span className="mt-3 h-2 w-2 flex-shrink-0 rounded-full bg-[#d99b16]" />

                                <div
                                  dangerouslySetInnerHTML={{
                                    __html: content,
                                  }}
                                />

                              </div>

                            ))}

                        </div>

                      </div>

                    </motion.div>

                  )}

                </AnimatePresence>

              </motion.div>
            );
          })}

        </div>

      </section>


      {/* ================= FOOTER ================= */}
      <section className="border-t bg-white py-8 text-center">

        <p className="text-sm text-gray-500">
          © {new Date().getFullYear()} Bandarban University
        </p>

      </section>

    </div>
  );
}